// Company stores (S4 shop, 2026-10-12) — /store/product/:id (the listing id).
//
// Photos (the rep's approved mock-up of the store's logo on the shown colour
// first, when there is one — 2026-10-19), the colours THIS store allows, and a size × quantity grid per
// colour, like the website's product page (sizes from the public
// GET /storefront/products/:id/colorways, in its order; "CUSTOM" is a
// supplier placeholder, never a size; no sizes → one "Quantity" box). The
// price is asked of the server as you type (POST /portal/store/cart/price,
// debounced) — each, total, shipping, and the server's own words when it
// can't be ordered as it is (a minimum, a quote-only option). "Add to cart"
// keeps this product's colours and sizes in the cart; opening the product
// again edits them.
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PortalLayout } from "@/components/PortalLayout";
import { StoreGate } from "@/components/StoreGate";
import { CARD, LINK, MoneyRow, Notice, PRIMARY, SECONDARY, useMyCart } from "@/components/StoreShopUi";
import { apiMessage, mockupFor, storeOffMessage, type MyStore, type StoreProduct } from "@/lib/portal-store";
import { setCartItem } from "@/lib/store-cart";
import { cleanItems, money, pieces, priceStoreCart, type ColourQuantities } from "@/lib/store-shop";

interface Colorway {
  name: string;
  sizes: string[];
  images: Array<{ src: string; label: string }>;
}

const realSize = (z: unknown): z is string => typeof z === "string" && z.trim() !== "" && z.trim().toUpperCase() !== "CUSTOM";
const ONE = "Quantity";

async function fetchColorways(productId: string): Promise<Colorway[]> {
  const r = await api<{ colorways: Colorway[] }>(`/storefront/products/${encodeURIComponent(productId)}/colorways`, { skipAuth: true });
  return r.colorways ?? [];
}

export function StoreProductPage() {
  const { id = "" } = useParams<{ id: string }>();
  return (
    <PortalLayout>
      <StoreGate>
        {(my) => {
          const product = my.products.find((p) => p.productId === id) ?? my.products.find((p) => p.id === id);
          if (!product) {
            return (
              <div className={CARD}>
                <h1 className="mb-2 font-display text-2xl font-bold tracking-tight">This item isn't in your store</h1>
                <p className="text-sm text-brown-400">
                  It may have been taken out of the store. <Link to="/store" className={LINK}>Back to the store</Link>
                </p>
              </div>
            );
          }
          return <ProductView key={product.productId} my={my} product={product} />;
        }}
      </StoreGate>
    </PortalLayout>
  );
}

function ProductView({ my, product }: { my: MyStore; product: StoreProduct }) {
  const { cart, update, ready } = useMyCart();
  const ways = useQuery({
    queryKey: ["storefront-colorways", product.productId],
    queryFn: () => fetchColorways(product.productId),
    staleTime: 10 * 60_000,
    retry: 1,
  });

  // The store's colours, with the sizes and photos the listing has for each.
  const colours = useMemo(() => {
    const list = ways.data ?? [];
    const names = product.colours.length ? product.colours : list.map((c) => c.name);
    return names.map((name) => {
      const w = list.find((c) => c.name === name);
      return { name, sizes: (w?.sizes ?? []).filter(realSize), images: w?.images ?? [] };
    });
  }, [ways.data, product.colours]);

  // Sizes in wearing order: the colour with the most sizes leads (website rule).
  const allSizes = useMemo(() => {
    const order: string[] = [];
    for (const c of [...colours].sort((a, b) => b.sizes.length - a.sizes.length)) for (const z of c.sizes) if (!order.includes(z)) order.push(z);
    return order.length ? order : [ONE];
  }, [colours]);
  const sizesFor = (colour: string) => {
    const own = colours.find((c) => c.name === colour)?.sizes ?? [];
    return own.length ? allSizes.filter((z) => own.includes(z)) : allSizes;
  };

  const inCart = cart.items.find((i) => i.productId === product.productId) ?? null;
  const [rows, setRows] = useState<ColourQuantities[] | null>(null);
  // Start from what's in the cart (editing), else the first colour.
  useEffect(() => {
    if (rows !== null || !ready) return;
    if (inCart) setRows(inCart.colorQuantities.map((r) => ({ color: r.color, quantities: { ...r.quantities } })));
    else if (product.colours[0] || colours[0]) setRows([{ color: product.colours[0] ?? colours[0]!.name, quantities: {} }]);
  }, [rows, ready, inCart, product.colours, colours]);
  const current = rows ?? [];

  const [shown, setShown] = useState<string | null>(null);
  const shownColour = shown ?? current[0]?.color ?? colours[0]?.name ?? null;
  // The rep's approved mock-up of this colour leads (never another colour's);
  // the plain photos follow it. No mock-up → the plain photos, as before.
  // A mock-up link that won't load falls back to the plain photos.
  const [mockupBroken, setMockupBroken] = useState(false);
  const mockup = mockupBroken ? null : mockupFor(product, shownColour);
  const photos = [
    ...(mockup ? [{ src: mockup.url, label: "Mock-up with your logo" }] : []),
    ...(colours.find((c) => c.name === shownColour)?.images ?? []),
  ];
  const [photoAt, setPhotoAt] = useState(0);
  const mainPhoto = photos[photoAt]?.src ?? photos[0]?.src ?? product.photo;
  const showingMockup = !!mockup && mainPhoto === mockup.url;

  const item = { productId: product.productId, colorQuantities: current };
  const clean = cleanItems([item]);
  const qty = clean.reduce((n, i) => n + i.colorQuantities.reduce((m, r) => m + Object.values(r.quantities).reduce((a, b) => a + b, 0), 0), 0);

  // Debounced live price — the server's numbers only.
  const sig = JSON.stringify(clean);
  const [debounced, setDebounced] = useState(sig);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(sig), 450);
    return () => window.clearTimeout(t);
  }, [sig]);
  const price = useQuery({
    queryKey: ["portal-store-price", debounced],
    queryFn: () => priceStoreCart(JSON.parse(debounced), null),
    enabled: qty > 0 && debounced === sig,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
  });
  const p = qty > 0 ? price.data : undefined;
  const settling = qty > 0 && (debounced !== sig || price.isFetching);

  const [added, setAddedState] = useState<null | "added" | "updated">(null);
  const paused = my.store.status !== "live";
  const blocked = p?.status === "blocked";
  const unused = colours.filter((c) => !current.some((r) => r.color === c.name));

  function setQty(rowAt: number, size: string, value: string) {
    const n = Math.max(0, Math.min(99_999, Math.floor(Number(value) || 0)));
    setAddedState(null);
    setRows(current.map((r, i) => (i === rowAt ? { ...r, quantities: { ...r.quantities, [size]: n } } : r)));
  }

  function addToCart() {
    setAddedState(inCart ? "updated" : "added");
    update((c) => setCartItem(c, item));
  }

  return (
    <div className="space-y-6">
      <Link to="/store" className="inline-block text-sm text-brown-400 hover:text-ink">
        ← {my.store.name}
      </Link>
      {/* Wide screens: the square photo stops growing at 42rem so it never fills the whole height (CEO 2026-10-19 full width). */}
      <div className="grid gap-6 lg:grid-cols-2 lg:gap-10 2xl:grid-cols-[minmax(0,42rem)_minmax(0,46rem)]">
        {/* Photos */}
        <div className="min-w-0">
          <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-lg border border-rule bg-white">
            {mainPhoto ? (
              <img
                src={mainPhoto}
                alt={`${product.name}${shownColour ? ` in ${shownColour}` : ""}${showingMockup ? " with your logo (mock-up)" : ""}`}
                className="h-full w-full object-contain p-4"
                onError={showingMockup ? () => { setMockupBroken(true); setPhotoAt(0); } : undefined}
              />
            ) : (
              <span className="text-xs text-muted">Photo coming soon</span>
            )}
            {showingMockup && (
              <span className="absolute bottom-3 left-3 rounded-full bg-cream-100 px-2.5 py-1 text-xs font-medium text-brown-500">Mock-up with your logo</span>
            )}
          </div>
          {photos.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {photos.map((ph, i) => (
                <button
                  key={ph.src}
                  type="button"
                  onClick={() => setPhotoAt(i)}
                  aria-label={ph.label || `Photo ${i + 1}`}
                  aria-pressed={i === photoAt}
                  className={`h-16 w-16 shrink-0 overflow-hidden rounded-md border bg-white ${i === photoAt ? "border-ink" : "border-rule"}`}
                >
                  <img src={ph.src} alt="" className="h-full w-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Choose */}
        <div className="min-w-0 space-y-5">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{product.name}</h1>
            {product.branding && <p className="mt-1 text-sm text-brown-400">Branded with your logo: {product.branding.charAt(0).toLowerCase() + product.branding.slice(1)}</p>}
          </div>

          {paused && <Notice tone="warn">{storeOffMessage(my.store, "browse")}</Notice>}
          {ways.isError && <Notice tone="warn">We couldn't load this item's sizes. Please refresh the page.</Notice>}

          {colours.length > 1 && (
            <div>
              <p className="mb-2 text-sm font-semibold text-ink">Colors</p>
              <div className="flex flex-wrap gap-2">
                {colours.map((c) => {
                  const chosen = current.some((r) => r.color === c.name);
                  return (
                    <button
                      key={c.name}
                      type="button"
                      onClick={() => {
                        setShown(c.name);
                        setPhotoAt(0);
                        if (!chosen) {
                          setAddedState(null);
                          setRows([...current, { color: c.name, quantities: {} }]);
                        }
                      }}
                      aria-pressed={chosen}
                      className={`min-h-[36px] rounded-full border px-3 text-sm ${chosen ? "border-ink bg-ink text-white" : "border-rule bg-white text-ink hover:bg-canvas"}`}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-brown-400">Tap a color to add it. Enter how many of each size.</p>
            </div>
          )}

          <div className="space-y-4">
            {ways.isPending && <div className="h-24 animate-pulse rounded-md bg-cream-300" aria-label="Loading sizes" />}
            {!ways.isPending &&
              current.map((row, at) => {
                const sizes = sizesFor(row.color);
                const rowQty = Object.values(row.quantities).reduce((a, b) => a + (b > 0 ? b : 0), 0);
                return (
                  <fieldset key={row.color} className="rounded-md border border-rule bg-white p-3 sm:p-4">
                    <legend className="sr-only">{row.color}</legend>
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <button type="button" className="truncate text-sm font-semibold text-ink" onClick={() => { setShown(row.color); setPhotoAt(0); }}>
                        {row.color}
                      </button>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className="text-xs text-brown-400">{pieces(rowQty)}</span>
                        {current.length > 1 && (
                          <button
                            type="button"
                            aria-label={`Remove ${row.color}`}
                            onClick={() => { setAddedState(null); setRows(current.filter((_, i) => i !== at)); }}
                            className="h-8 w-8 rounded-md border border-rule text-ink hover:bg-canvas"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                      {sizes.map((z) => (
                        <label key={z} className="flex min-w-0 flex-col gap-1 text-xs text-brown-400">
                          {z}
                          <input
                            type="number"
                            inputMode="numeric"
                            min={0}
                            max={99999}
                            step={1}
                            value={row.quantities[z] ? String(row.quantities[z]) : ""}
                            placeholder="0"
                            aria-label={`${row.color} ${z === ONE ? "quantity" : `size ${z}`}`}
                            onChange={(e) => setQty(at, z, e.target.value)}
                            className="w-full min-w-0 rounded-md border border-cream-300 bg-white px-2 py-2 text-center text-sm text-brown-500 focus:border-peach-400 focus:outline-none"
                          />
                        </label>
                      ))}
                    </div>
                  </fieldset>
                );
              })}
            {!ways.isPending && unused.length > 0 && current.length > 0 && colours.length > 1 && (
              <p className="text-xs text-brown-400">Want another color too? Tap it above.</p>
            )}
          </div>

          {/* The server's price */}
          <div className={CARD} aria-live="polite">
            {qty === 0 ? (
              <p className="text-sm text-brown-400">Enter your sizes to see your price.</p>
            ) : !p ? (
              price.isError ? (
                <p className="text-sm text-rust-500">{apiMessage(price.error, "We couldn't price this right now. Please try again.")}</p>
              ) : (
                <p className="text-sm text-brown-400">Working out your price…</p>
              )
            ) : p.status === "blocked" ? (
              <p className="text-sm text-rust-500">{p.message}</p>
            ) : (
              <dl className={`space-y-1 ${settling ? "opacity-60" : ""}`}>
                {p.items[0] && <MoneyRow label="Each" value={money(p.items[0].each)} />}
                {p.items[0]?.setup ? <MoneyRow label="Setup, once per order" value={money(p.items[0].setup)} /> : null}
                <MoneyRow label={`Total for ${pieces(p.items[0]?.quantity ?? qty)}`} value={money(p.subtotal)} strong />
                <MoneyRow
                  label="Shipping"
                  value={p.shippingNeedsAddress ? "Added in your cart" : p.shipping > 0 ? money(p.shipping) : "Included"}
                />
                {p.items[0]?.notice && <p className="pt-2 text-xs text-brown-400">{p.items[0].notice}</p>}
              </dl>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button type="button" className={PRIMARY} disabled={paused || qty === 0 || blocked || !ready} onClick={addToCart}>
              {inCart ? "Update cart" : "Add to cart"}
            </button>
            {inCart && (
              <Link to="/store/cart" className={SECONDARY}>
                Go to cart
              </Link>
            )}
          </div>
          {added && (
            <p role="status" className="text-sm text-ok">
              {added === "updated" ? "Your cart is up to date." : "Added to your cart."} <Link to="/store/cart" className={LINK}>Check out</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
